import { lazy as reactLazy, type ComponentType } from "react";

/**
 * A publish replaces every hashed chunk file. Tabs still running the previous
 * build then request chunks that no longer exist, the dynamic import rejects,
 * and the crash screen replaces the page. These helpers recover instead.
 */

const RELOAD_GUARD_KEY = "podgo_chunk_reload";

/** Heuristic match for "the JS module could not be fetched/parsed". */
export function isChunkLoadError(error: unknown): boolean {
  const message =
    (error instanceof Error ? `${error.name} ${error.message}` : String(error ?? "")) || "";
  return /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|ChunkLoadError|Loading chunk \d+ failed|Loading CSS chunk/i.test(
    message,
  );
}

/**
 * Reload once with a cache-busting param so intermediaries hand back the new
 * index.html. Guarded per-session so a genuinely broken build cannot loop.
 * Returns false when a reload was already attempted.
 */
export function reloadForStaleChunk(): boolean {
  try {
    if (sessionStorage.getItem(RELOAD_GUARD_KEY)) return false;
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  } catch {
    // Storage unavailable (private mode): fall through and reload once anyway.
  }
  const url = new URL(window.location.href);
  url.searchParams.set("_r", String(Date.now()));
  window.location.replace(url.toString());
  return true;
}

/** Clear the guard once the app has rendered successfully on the new build. */
export function clearStaleChunkGuard(): void {
  try {
    sessionStorage.removeItem(RELOAD_GUARD_KEY);
  } catch {
    // Ignore — the guard simply stays until the session ends.
  }
}

/**
 * Drop-in replacement for React.lazy: retries the import once, then recovers a
 * stale build with a single reload rather than surfacing the crash screen.
 */
export function lazyWithRetry<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
) {
  return reactLazy(async () => {
    try {
      return await factory();
    } catch (error) {
      if (!isChunkLoadError(error)) throw error;
      try {
        // Transient network blip: one immediate retry is usually enough.
        return await factory();
      } catch (retryError) {
        if (reloadForStaleChunk()) {
          // Keep Suspense pending while the browser navigates away.
          return await new Promise<{ default: T }>(() => {});
        }
        throw retryError;
      }
    }
  });
}
